import { FC, FocusEvent, memo, useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { BrandLogo } from './icons';
import { SideMenuGroup } from './SideMenuGroup';
import styles from './style.module.css';
import type { SideMenuGroup as SideMenuGroupType } from './types';

interface Props {
  groups: SideMenuGroupType[];
  activeKey: string | null;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const SideMenuComponent: FC<Props> = ({
  groups,
  activeKey,
  defaultExpanded = false,
  expanded: expandedProp,
  onExpandedChange,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const setExpanded = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setInternalExpanded(next);
      }
      onExpandedChange?.(next);
    },
    [isControlled, onExpandedChange],
  );

  const handleMouseEnter = useCallback(() => setExpanded(true), [setExpanded]);
  const handleMouseLeave = useCallback(() => setExpanded(false), [setExpanded]);
  const handleFocus = useCallback(() => setExpanded(true), [setExpanded]);
  const handleBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setExpanded(false);
      }
    },
    [setExpanded],
  );

  const visibleGroups = useMemo(() => {
    return groups
      .map((group) => {
        const roleFilteredItems = group.path
          ? group.items
          : group.items.filter((item) => Boolean(item.path?.trim()));

        return {
          ...group,
          items: roleFilteredItems,
        };
      })
      .filter((group) => Boolean(group.path) || group.items.length > 0);
  }, [groups]);

  const topGroups = visibleGroups.filter((g) => g.placement !== 'bottom');
  const bottomGroups = visibleGroups.filter((g) => g.placement === 'bottom');

  return (
    <aside
      className={cn(styles.sideMenu, { [styles.expanded]: expanded })}
      data-expanded={expanded}
    >
      <nav
        className={styles.panel}
        aria-label="Главная навигация"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        <div className={styles.brand}>
          <BrandLogo />
          <span className={styles.brandText} aria-hidden={!expanded}>
            <strong>Med</strong>
            <span>Vox</span>
          </span>
          <span className={styles.brandSubtitle} aria-hidden={!expanded}>
            s2snext
          </span>
        </div>

        <div className={styles.scrollArea}>
          <div className={styles.groupList}>
            {topGroups.map((group) => (
              <SideMenuGroup
                key={group.key}
                group={group}
                expanded={expanded}
                activeKey={activeKey}
              />
            ))}
          </div>

          {bottomGroups.length > 0 && (
            <div className={cn(styles.groupList, styles.bottomGroupList)}>
              {bottomGroups.map((group) => (
                <SideMenuGroup
                  key={group.key}
                  group={group}
                  expanded={expanded}
                  activeKey={activeKey}
                />
              ))}
            </div>
          )}
        </div>
      </nav>
    </aside>
  );
};

export const SideMenu = memo(SideMenuComponent);
SideMenu.displayName = 'SideMenu';
